'use strict';

/**
 * フォーラム投稿本文の格納長 (forum_messages.msg_text のバイト数) の目安を計算する。
 *
 * サーバ側の Mal\Helper\Forum\MessageText と同じ計算の JS 版 (CU-z8xb3uqxn2)。
 * 保存されるのは BBCode::normalize() 後の文字列で、
 * - htmlentities(ENT_QUOTES | ENT_HTML401) で & → &amp;、é → &eacute; などに膨らむ
 * - 改行ごとに "<br />" が挿入される (ブラウザは改行を CRLF で送信する)
 * - 接続 charset が latin1・テーブルが utf8mb3 のため、0x80 以上の各バイトが 2〜3 byte になる
 *   (UTF-8 で 3 byte の日本語 1 文字は 7 byte 前後、絵文字は 9 byte 前後)
 * BBCode の再構築による差は無視するので、あくまで目安。上限の判定はサーバ側で行う。
 */

export const MAX_BYTES = 65535;

/** 何割を超えたら残りを表示するか */
export const SHOW_RATIO = 0.9;

/** cp1252 で utf8mb3 にすると 3 byte になるバイト (それ以外の 0x80〜0xFF は 2 byte) */
const THREE_BYTE_CHARS = [
  0x80, 0x82, 0x84, 0x85, 0x86, 0x87, 0x89, 0x8B,
  0x91, 0x92, 0x93, 0x94, 0x95, 0x96, 0x97, 0x99, 0x9B,
];

const ASCII_ENTITY_LENGTHS = {
  '&': 5, // &amp;
  '"': 6, // &quot;
  "'": 6, // &#039;
  '<': 4, // &lt;
  '>': 4, // &gt;
};

/**
 * htmlentities(ENT_HTML401) が名前付き実体参照にする非 ASCII 文字。キーは実体参照の長さ
 * (PHP の get_html_translation_table(HTML_ENTITIES, ENT_QUOTES | ENT_HTML401, 'UTF-8') から生成)
 */
const NAMED_ENTITY_CODE_POINTS = {
  4: [
    0x39C, 0x39D, 0x39E, 0x3A0, 0x3BC, 0x3BD, 0x3BE, 0x3C0, 0x220B, 0x2228,
    0x2260, 0x2264, 0x2265,
  ],
  5: [
    0xA5, 0xA8, 0xAC, 0xAD, 0xAE, 0xB0, 0xD0, 0xF0, 0x397, 0x3A1,
    0x3A4, 0x3A6, 0x3A7, 0x3A8, 0x3B7, 0x3C1, 0x3C4, 0x3C6, 0x3C7, 0x3C8,
    0x3D6, 0x200D, 0x200E, 0x200F, 0x2211, 0x2220, 0x2227, 0x2229, 0x222A, 0x222B,
    0x223C, 0x2282, 0x2283, 0x25CA,
  ],
  6: [
    0xA0, 0xA2, 0xA7, 0xA9, 0xAA, 0xAF, 0xB2, 0xB3, 0xB6, 0xB9,
    0xBA, 0xC4, 0xCB, 0xCF, 0xD6, 0xDC, 0xE4, 0xEB, 0xEF, 0xF6,
    0xFC, 0xFF, 0x178, 0x192, 0x2C6, 0x392, 0x396, 0x399, 0x3B2, 0x3B6,
    0x3B9, 0x2002, 0x2003, 0x200C, 0x2022, 0x20AC, 0x211C, 0x2190, 0x2191, 0x2192,
    0x2193, 0x2194, 0x21D0, 0x21D1, 0x21D2, 0x21D3, 0x21D4, 0x2202, 0x2208, 0x220F,
    0x221D, 0x2245, 0x2284, 0x2286, 0x2287, 0x22A5, 0x22C5, 0x2329, 0x232A,
  ],
  7: [
    0xA1, 0xA3, 0xAB, 0xB4, 0xB5, 0xB8, 0xBB, 0xC2, 0xC5, 0xC6,
    0xCA, 0xCE, 0xD4, 0xD7, 0xDB, 0xDE, 0xDF, 0xE2, 0xE5, 0xE6,
    0xEA, 0xEE, 0xF4, 0xFB, 0xFE, 0x152, 0x153, 0x2DC, 0x391, 0x393,
    0x394, 0x398, 0x39A, 0x3A3, 0x3A9, 0x3B1, 0x3B3, 0x3B4, 0x3B8, 0x3BA,
    0x3C3, 0x3C9, 0x3D2, 0x2013, 0x2014, 0x2018, 0x2019, 0x201A, 0x201C, 0x201D,
    0x201E, 0x2032, 0x2033, 0x203E, 0x2044, 0x2111, 0x2122, 0x21B5, 0x2203, 0x2205,
    0x2207, 0x2209, 0x2212, 0x221A, 0x221E, 0x2248, 0x2261, 0x2295, 0x2308, 0x2309,
    0x2663, 0x2666,
  ],
  8: [
    0xA4, 0xA6, 0xB1, 0xB7, 0xBC, 0xBD, 0xBE, 0xBF, 0xC0, 0xC1,
    0xC3, 0xC7, 0xC8, 0xC9, 0xCC, 0xCD, 0xD1, 0xD2, 0xD3, 0xD5,
    0xD8, 0xD9, 0xDA, 0xDD, 0xE0, 0xE1, 0xE3, 0xE7, 0xE8, 0xE9,
    0xEC, 0xED, 0xF1, 0xF2, 0xF3, 0xF5, 0xF7, 0xF8, 0xF9, 0xFA,
    0xFD, 0x160, 0x161, 0x39B, 0x3BB, 0x3C2, 0x2009, 0x2020, 0x2021, 0x2026,
    0x2030, 0x2039, 0x203A, 0x2118, 0x2200, 0x2217, 0x2234, 0x2297, 0x230A, 0x230B,
    0x2660, 0x2665,
  ],
  9: [
    0x395, 0x39F, 0x3A5, 0x3B5, 0x3BF, 0x3C5, 0x2135,
  ],
  10: [
    0x3D1,
  ],
};

const NAMED_ENTITY_LENGTHS = (function() {
  const map = {};
  Object.keys(NAMED_ENTITY_CODE_POINTS).forEach(function(length) {
    NAMED_ENTITY_CODE_POINTS[length].forEach(function(codePoint) {
      map[codePoint] = parseInt(length, 10);
    });
  });
  return map;
})();

function storedByteLength(byte) {
  if (byte < 0x80) return 1;
  return THREE_BYTE_CHARS.indexOf(byte) >= 0 ? 3 : 2;
}

function utf8Bytes(codePoint) {
  if (codePoint < 0x80) return [codePoint];
  if (codePoint < 0x800) {
    return [0xC0 | (codePoint >> 6), 0x80 | (codePoint & 0x3F)];
  }
  if (codePoint < 0x10000) {
    return [0xE0 | (codePoint >> 12), 0x80 | ((codePoint >> 6) & 0x3F), 0x80 | (codePoint & 0x3F)];
  }
  return [
    0xF0 | (codePoint >> 18),
    0x80 | ((codePoint >> 12) & 0x3F),
    0x80 | ((codePoint >> 6) & 0x3F),
    0x80 | (codePoint & 0x3F),
  ];
}

/**
 * @param {string} text textarea / エディタの値
 * @return {number} 保存時のバイト数の目安
 */
export function estimateStoredLength(text) {
  const value = String(text || '').replace(/\r\n?/g, '\n');
  let length = 0;
  for (let i = 0; i < value.length; i++) {
    let codePoint = value.charCodeAt(i);
    // サロゲートペア
    if (codePoint >= 0xD800 && codePoint <= 0xDBFF && i + 1 < value.length) {
      const low = value.charCodeAt(i + 1);
      if (low >= 0xDC00 && low <= 0xDFFF) {
        codePoint = (codePoint - 0xD800) * 0x400 + (low - 0xDC00) + 0x10000;
        i++;
      }
    }
    if (codePoint === 0x0A) {
      length += 8; // "<br />" + CRLF
      continue;
    }
    const ch = value.charAt(i);
    if (codePoint < 0x80) {
      length += ASCII_ENTITY_LENGTHS[ch] || 1;
      continue;
    }
    if (NAMED_ENTITY_LENGTHS[codePoint]) {
      length += NAMED_ENTITY_LENGTHS[codePoint];
      continue;
    }
    utf8Bytes(codePoint).forEach(function(byte) {
      length += storedByteLength(byte);
    });
  }
  return length;
}

/**
 * @param {number} storedLength
 * @param {number} [maxBytes]
 * @return {{visible: boolean, over: boolean, text: string}}
 */
export function describe(storedLength, maxBytes = MAX_BYTES) {
  const remaining = maxBytes - storedLength;
  if (remaining < 0) {
    return {
      visible: true,
      over: true,
      text: 'Your message is too long and cannot be posted (about ' + storedLength.toLocaleString('en-US') +
        ' / ' + maxBytes.toLocaleString('en-US') + '). Please shorten it or split it into multiple posts.',
    };
  }
  if (storedLength < maxBytes * SHOW_RATIO) {
    return {visible: false, over: false, text: ''};
  }
  return {
    visible: true,
    over: false,
    text: 'About ' + remaining.toLocaleString('en-US') + ' characters left' +
      ' (line breaks, symbols, accented letters, non-Latin characters and emoji count as several).',
  };
}
